import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import PaymentIcon from '@mui/icons-material/Payment'
import AdminPage from '../../../../components/page/AdminPage'
import { loadStripeTerminal } from '@stripe/terminal-js'
import UserSearch from '../components/UserSearch'
import RecentTransactions from '../components/RecentTransactions'
import TerminalStatus from '../components/TerminalStatus'

function Terminal() {
  const [amount, setAmount] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [status, setStatus] = useState(null)
  const [terminal, setTerminal] = useState(null)
  const [reader, setReader] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('initializing')
  const [lastTransaction, setLastTransaction] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const transactionsRef = useRef()

  useEffect(() => {
    initializeTerminal()

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const initializeTerminal = async () => {
    try {
      setStatus({ type: 'info', message: 'Initializing terminal...' })

      const StripeTerminal = await loadStripeTerminal()

      const terminalInstance = StripeTerminal.create({
        onFetchConnectionToken: fetchConnectionToken,
        onUnexpectedReaderDisconnect: () => {
          setConnectionStatus('disconnected')
          setStatus({
            type: 'warning',
            message: 'Reader disconnected unexpectedly',
          })
        },
      })

      setTerminal(terminalInstance)
      console.log(
  '📡 Stripe Terminal Location:',
  process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID
)
      await discoverReaders(terminalInstance)
    } catch (error) {
      console.error('Error initializing terminal:', error)
      setStatus({ type: 'error', message: 'Failed to initialize terminal' })
      setConnectionStatus('error')
    }
  }

  const fetchConnectionToken = async () => {
    try {
      const response = await fetch(
        'https://us-central1-train-hyve.cloudfunctions.net/createTerminalConnectionToken',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )
      const data = await response.json()
      return data.secret
    } catch (error) {
      console.error('Error fetching connection token:', error)
      throw error
    }
  }

  const discoverReaders = async (terminalInstance) => {
    try {
      setStatus({ type: 'info', message: 'Discovering readers...' })
      setConnectionStatus('discovering')

      const discoverResult = await terminalInstance.discoverReaders({
        simulated: process.env.NODE_ENV === 'development',
        location:
          process.env.NODE_ENV === 'development'
            ? undefined
            : process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID,
      })

      if (discoverResult.error) {
        console.error('Discover error:', discoverResult.error)
        setStatus({ type: 'error', message: discoverResult.error.message })
        setConnectionStatus('error')
        return
      }

      const discoveredReaders = discoverResult.discoveredReaders
      console.log(discoverResult)
      if (discoveredReaders.length === 0) {
        setStatus({
          type: 'warning',
          message:
            'No readers found. Please check your terminal is powered on and connected.',
        })
        setConnectionStatus('no_readers')
        return
      }

      // Auto-connect to the first reader
      await connectToReader(terminalInstance, discoveredReaders[0])
    } catch (error) {
      console.error('Error discovering readers:', error)
      setStatus({ type: 'error', message: 'Failed to discover readers' })
      setConnectionStatus('error')
    }
  }

  const connectToReader = async (terminalInstance, selectedReader) => {
    try {
      setStatus({ type: 'info', message: 'Connecting to reader...' })
      setConnectionStatus('connecting')

      const connectResult = await terminalInstance.connectReader(selectedReader)

      if (connectResult.error) {
        console.error('Connect error:', connectResult.error)
        setStatus({ type: 'error', message: connectResult.error.message })
        setConnectionStatus('error')
        return
      }

      setReader(connectResult.reader)
      setConnectionStatus('connected')
      setStatus({
        type: 'success',
        message: `Connected to ${connectResult.reader.label || 'Terminal'}`,
      })
    } catch (error) {
      console.error('Error connecting to reader:', error)
      setStatus({ type: 'error', message: 'Failed to connect to reader' })
      setConnectionStatus('error')
    }
  }

  const handleAmountChange = (event) => {
    const value = event.target.value
    // Only allow numbers and decimal point
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value)
    }
  }

  const handleQuickAmount = (quickAmount) => {
    setAmount(quickAmount.toString())
  }

  const handleProcessPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setStatus({ type: 'error', message: 'Please enter a valid amount' })
      return
    }

    if (!terminal || connectionStatus !== 'connected') {
      setStatus({ type: 'error', message: 'Terminal not connected' })
      return
    }

    try {
      setIsProcessing(true)
      setStatus({ type: 'info', message: 'Creating payment intent...' })

      // Create payment intent
      const response = await fetch(
        'https://us-central1-train-hyve.cloudfunctions.net/createTerminalPaymentIntent',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: parseFloat(amount),
            description: `Terminal payment - £${amount}`,
          }),
        }
      )

      const { client_secret, id: paymentIntentId } = await response.json()

      setStatus({
        type: 'info',
        message: 'Please present payment method to terminal...',
      })

      // Collect payment method
      const collectResult = await terminal.collectPaymentMethod(client_secret)

      if (collectResult.error) {
        throw new Error(collectResult.error.message)
      }

      setStatus({ type: 'info', message: 'Processing payment...' })

      // Process payment
      const confirmResult = await terminal.processPayment(
        collectResult.paymentIntent
      )

      if (confirmResult.error) {
        throw new Error(confirmResult.error.message)
      }

      // Log payment to Firestore
      await fetch(
        'https://us-central1-train-hyve.cloudfunctions.net/logTerminalPayment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId,
            user: selectedUser
              ? {
                  id: selectedUser.id,
                  firstName: selectedUser.firstName,
                  lastName: selectedUser.lastName,
                  email: selectedUser.email,
                }
              : null,
          }),
        }
      )

      setStatus({
        type: 'success',
        message: `Payment of £${amount} processed successfully!`,
      })
      setLastTransaction({
        amount: parseFloat(amount),
        time: new Date().toLocaleTimeString(),
        id: paymentIntentId.slice(-8),
        user: selectedUser,
      })
      setAmount('')
      setSelectedUser(null) // Clear user selection after payment

      // Refresh transactions list
      if (transactionsRef.current) {
        transactionsRef.current.refresh()
      }
    } catch (error) {
      console.error('Payment error:', error)
      setStatus({ type: 'error', message: error.message || 'Payment failed' })
    } finally {
      setIsProcessing(false)
    }
  }

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'success.main'
      case 'connecting':
      case 'discovering':
      case 'initializing':
        return 'warning.main'
      default:
        return 'error.main'
    }
  }

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return reader
          ? `Connected to ${reader.label || 'Terminal'}`
          : 'Connected'
      case 'connecting':
        return 'Connecting...'
      case 'discovering':
        return 'Discovering readers...'
      case 'initializing':
        return 'Initializing...'
      case 'no_readers':
        return 'No readers found'
      default:
        return 'Connection error'
    }
  }

  const quickAmounts = [5, 10, 15, 20, 25, 30, 50, 100]

  return (
    <AdminPage>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
        <Grid container spacing={3}>
          {/* Payment Input Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Enter Payment Amount
                </Typography>

                <TextField
                  fullWidth
                  label='Amount'
                  variant='outlined'
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder='0.00'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>£</InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                  disabled={isProcessing}
                />

                <Typography variant='body2' color='text.secondary' gutterBottom>
                  Quick amounts:
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {quickAmounts.map((quickAmount) => (
                    <Button
                      key={quickAmount}
                      variant='outlined'
                      size='small'
                      onClick={() => handleQuickAmount(quickAmount)}
                      disabled={isProcessing}
                    >
                      £{quickAmount}
                    </Button>
                  ))}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <UserSearch
                    selectedUser={selectedUser}
                    onUserSelect={setSelectedUser}
                    disabled={isProcessing}
                  />
                </Box>

                <Button
                  fullWidth
                  variant='contained'
                  size='large'
                  onClick={handleProcessPayment}
                  disabled={
                    !amount ||
                    parseFloat(amount) <= 0 ||
                    isProcessing ||
                    connectionStatus !== 'connected'
                  }
                  startIcon={
                    isProcessing ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : (
                      <PaymentIcon />
                    )
                  }
                  sx={{ py: 2 }}
                >
                  {isProcessing
                    ? 'Processing...'
                    : `Charge £${amount || '0.00'}`}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Terminal Status Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Terminal Status
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Reader Status:
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: 'bold',
                      color: getConnectionStatusColor(),
                    }}
                  >
                    {getConnectionStatusText()}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant='body2' color='text.secondary'>
                    Last Transaction:
                  </Typography>
                  {lastTransaction ? (
                    <Box>
                      <Typography variant='body1'>
                        £{lastTransaction.amount.toFixed(2)} at{' '}
                        {lastTransaction.time}
                      </Typography>
                      {lastTransaction.user && (
                        <Typography variant='body2' color='primary.main'>
                          {lastTransaction.user.firstName}{' '}
                          {lastTransaction.user.lastName}
                        </Typography>
                      )}
                      <Typography variant='body2' color='text.secondary'>
                        ID: {lastTransaction.id}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant='body1'>
                      No recent transactions
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Terminal Status Display */}
            <Box sx={{ mt: 2 }}>
              <TerminalStatus status={status} isProcessing={isProcessing} />
            </Box>
          </Grid>
        </Grid>

        {/* Transaction History */}
        <Box sx={{ mt: 3 }}>
          <RecentTransactions ref={transactionsRef} />
        </Box>
      </Box>
    </AdminPage>
  )
}

export default Terminal
